import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Typography, Space, Tag } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";

interface SummaryData {
  headline: string;
  description: string;
  evidence: string[];
}

// Summary Block Recipe
const summaryRecipe = {
  id: "summary-block",
  name: "Summary Block",
  globalKey: [],
  prefix: `{
    "headline": "string — From 'summary_analysis' and 'trend_analysis'. Write a short, clear headline that highlights a key finding or insight.",
    "description": "string — From 'insight_generation' and 'trend_analysis'. 3-5 sentence narrative of the analysis, findings, and highlights. Bold key words.",
    "evidence": ["string — Supporting evidence for the summary"]
  }`,
  format: {
    type: "json",
    fields: ["headline", "description", "evidence"],
    constraints: [
      { field: "headline", type: "string", required: true },
      { field: "description", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as SummaryData;
          return Boolean(
            parsed.headline &&
              parsed.description &&
              Array.isArray(parsed.evidence)
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Summary Block Parser
function summaryParser(data: any) {
  try {
    return {
      data: data[summaryBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse summary data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Summary Block Variants
const summaryVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard summary display with all sections",
    defaultSettings: {
      showEvidence: true,
      layout: "vertical",
      emphasis: "headline",
    },
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal summary display with only headline and description",
    defaultSettings: {
      showEvidence: false,
      layout: "vertical",
      emphasis: "description",
    },
  },
  {
    id: "modern",
    name: "Modern",
    description: "Modern summary display with enhanced styling",
    defaultSettings: {
      showEvidence: true,
      layout: "horizontal",
      emphasis: "balanced",
    },
  },
  {
    id: "timeline",
    name: "Timeline",
    description: "Creative timeline-style summary display",
    defaultSettings: {
      showEvidence: true,
      layout: "timeline",
      emphasis: "headline",
    },
  },
];

// Summary Block Renderer
function SummaryBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const summaryData = data[summaryBlock.id];
  const variantSettings = summaryVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;

  if (!summaryData) return null;

  const renderTimeline = () => (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <div
        style={{
          borderLeft: "2px solid var(--color-primary)",
          paddingLeft: "var(--padding-3)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "-8px",
            top: "0",
            width: "14px",
            height: "14px",
            borderRadius: "50%",
            backgroundColor: "var(--color-primary)",
          }}
        />
        <Typography.Title level={3} className="vzbl-header-3">
          {stripMarkdown(summaryData.headline)}
        </Typography.Title>
      </div>

      <div
        style={{
          borderLeft: "2px solid var(--color-primary)",
          paddingLeft: "var(--padding-3)",
          marginLeft: "6px",
        }}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {summaryData.description}
        </Markdown>
      </div>

      {variantSettings?.showEvidence && summaryData.evidence?.length > 0 && (
        <div
          style={{
            borderLeft: "2px solid var(--color-primary)",
            paddingLeft: "var(--padding-3)",
            marginLeft: "6px",
          }}
        >
          <Typography.Text className="vzbl-text-3" type="secondary">
            Evidence:
          </Typography.Text>
          <Space wrap style={{ marginTop: "var(--padding-2)" }}>
            <ul>
              {summaryData.evidence.map((item: string, index: number) => (
                <li key={`evidence-${index}`}>
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    skipHtml={true}
                    className="vzbl-text-3"
                  >
                    {item}
                  </Markdown>
                </li>
              ))}
            </ul>
          </Space>
        </div>
      )}
    </Space>
  );

  const renderHorizontal = () => (
    <Space direction="horizontal" size="large" style={{ width: "100%" }}>
      <div style={{ flex: 1 }}>
        <Typography.Title level={3} className="vzbl-header-3">
          {stripMarkdown(summaryData.headline)}
        </Typography.Title>
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {summaryData.description}
        </Markdown>
      </div>
      {variantSettings?.showEvidence && summaryData.evidence?.length > 0 && (
        <div style={{ flex: 1 }}>
          <Typography.Text className="vzbl-text-3" type="secondary">
            Evidence:
          </Typography.Text>
          <Space wrap style={{ marginTop: "var(--padding-2)" }}>
            {summaryData.evidence.map((item: string, index: number) => (
              <Tag
                key={`evidence-${index}`}
                color="var(--color-pill-blue)"
                style={{
                  color: "var(--color-pill-blue-accent)",
                  borderColor: "var(--color-pill-blue-accent)",
                }}
              >
                {item}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </Space>
  );

  const renderVertical = () => (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Title
        level={variantSettings?.emphasis === "headline" ? 3 : 4}
        className="vzbl-header-3"
      >
        {stripMarkdown(summaryData.headline)}
      </Typography.Title>

      <Markdown
        remarkPlugins={[remarkGfm]}
        skipHtml={true}
        className={`vzbl-text-3 ${
          variantSettings?.emphasis === "description"
            ? "vzbl-text-emphasis"
            : ""
        }`}
      >
        {summaryData.description}
      </Markdown>

      {variantSettings?.showEvidence && summaryData.evidence?.length > 0 && (
        <Space direction="vertical" size="small">
          <Typography.Text className="vzbl-text-3" type="secondary">
            Evidence:
          </Typography.Text>
          <Space wrap>
            {summaryData.evidence.map((item: string, index: number) => (
              <Tag
                key={`evidence-${index}`}
                color="var(--color-pill-blue)"
                style={{
                  color: "var(--color-pill-blue-accent)",
                  borderColor: "var(--color-pill-blue-accent)",
                }}
              >
                {item}
              </Tag>
            ))}
          </Space>
        </Space>
      )}
    </Space>
  );

  let renderCard;
  switch (variantSettings?.layout) {
    case "timeline": {
      renderCard = renderTimeline();
      break;
    }
    case "horizontal": {
      renderCard = renderHorizontal();
      break;
    }
    default: {
      renderCard = renderVertical();
    }
  }

  return (
    <div className="vzbl-panel-container">
      <div className="vzbl-panel-shadow vzbl-panel-overlay">{renderCard}</div>
    </div>
  );
}

// Create Summary Block
const summaryBlock = createBaseBlock({
  id: "summary",
  name: "Summary Block",
  description: "Displays a summary with headline, description, and evidence",
  recipe: summaryRecipe,
  variants: summaryVariants,
  parser: summaryParser,
  renderer: SummaryBlockRenderer,
});

// Export the block for direct use
export { summaryBlock };
